import { type PlanListPage, PlanListPageNumberSchema } from '@developerz/domain';
import { IconPlus } from '@developerz/ui';
import { A, useSearchParams } from '@solidjs/router';
import { createEffect, createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { CustomPlansSection } from '../components/CustomPlansSection';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Pager } from '../components/Pager';
import { Panel } from '../components/Panel';
import { PlanCard } from '../components/PlanCard';
import { QueryBoundary } from '../components/QueryBoundary';
import { StartPlanModal } from '../components/StartPlanModal';
import { Button, buttonClass } from '../components/ui/Button';
import { t, tPlural } from '../i18n';
import { orgHref } from '../lib/org';
import { pastTheEndResetting, serverPageNav } from '../lib/paginate';
import { usePlans } from '../lib/plan';
import { SESSION_TOKEN } from '../lib/session';

// Plans (epics) index — the human door onto `GET /v1/plans` and the MCP
// `plan list` action. Container: it owns the read; <PlanCard/> paints a row.
//
// Until now the epic view was reachable ONLY by plan id, from a task row that
// happened to belong to one: an account's epics existed on two surfaces and on
// neither could a person answer "which plans do I have". This is that page, and
// it is the whole of `plan.list` — the capability has no other shape.
//
// A WATCH ROW OF ITS OWN (the canvas rail, #4195), under a Fleet path.
// `/fleet/plans` nests under Fleet because an epic is a fold over fleet tasks;
// the rail lists it separately because "which plans are running" is watched on
// its own. The Fleet header links in; this header links back; the epic view's
// back button lands HERE rather than skipping its own index.
//
// NUMBERED SERVER-SIDE PAGING, 100 PER PAGE (#5136). `GET /v1/plans` answers a
// `PlanListPage` envelope (`page`, `per_page`, `total`, `counts`, `repos`) and
// this route pages it server-side via `?page=` (a non-positive-integer or
// missing value falls back to 1) — no client-side cursor, no Load-more — and
// reads `total`/`counts`/`per_page` straight off the envelope for the heading
// and the pager. The pager renders nothing on a single page; a past-the-end
// `?page=` resets to 1 with replace nav so a REAL past-the-end bookmark
// self-heals — but the port echoes back whatever page was asked for rather
// than clamping it, so a genuinely EMPTY account opened on a stale `?page=`
// answers `{ plans: [], page: N, total: 0 }` for any N, and the reset effect
// never fires for it (it requires `total > 0`). `isEmpty` below checks
// `data.total === 0` directly for that shape, rather than depending on the
// reset to ever land it back on page 1 first (#5264 review).
//
// `page` is a `createMemo` over the URL, never a signal an effect copies into:
// a `?page=N` deep link's FIRST `usePlans` fetch already carries page=N, with
// no page=1 hop ahead of it, and `?page=abc` reads as page 1 on every request,
// not just the first one a naive read happens to catch.
//
// STARTING one is offered HERE, and only since `POST /v1/plans` landed. The act
// is `plan.start` — one `PlanStarter` behind two doors, the flat `plan_start`
// MCP verb and now the REST route this button calls — so the screen closes the
// last surface gap on that capability rather than adding a third path. What it
// must not do is overclaim: the 202 answers a scout TASK, the run id arrives
// when a box claims it and the plan itself lands minutes later on
// `plan_authored`, so the result reads dispatched-then-claimed and points at the
// queue board (`lib/plan-start.ts` carries the whole reasoning).
//
// THE START IS NOT ROLE-GATED CLIENT-SIDE. `write:tasks` is the same scope the
// queue board's "New task" spends and that form mirrors nothing either: the API
// is the authority and its 403 renders inline, never a control that silently was
// not there.

export function Plans(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session".
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  // `?page=` is the route's only paging state — a positive integer, or 1.
  // Anything else (missing, non-integer, `<= 0`) reads as page 1, the same way
  // REST's own `z.coerce.number()` then `PlanListPageNumberSchema` rejects the
  // bad value server-side. Reads are reactive so the past-the-end effect below
  // re-derives when the URL changes.
  const [params, setParams] = useSearchParams<{ page?: string }>();
  const pageFromUrl = (): number => {
    const raw = params.page;
    if (typeof raw !== 'string') return 1;
    // The same schema `lib/custom-plans.ts`'s parser checks `?custom_page=`
    // against — one bound, not a hand-rolled copy of it.
    const parsed = PlanListPageNumberSchema.safeParse(Number(raw));
    return parsed.success ? parsed.data : 1;
  };
  // Derived, not mirrored: a signal-plus-effect copy of the URL starts at its
  // OWN default (1) and only catches up once the effect runs, so a `?page=3`
  // deep link's first render — and its first `usePlans` fetch — would ask for
  // page 1, one wasted request ahead of the real one. A memo reads straight
  // off the URL on every render, so there is no window in which it disagrees
  // with it.
  const page = createMemo(pageFromUrl);

  // Push-navigation on a user click (back goes to the prior page); replace-
  // navigation on the past-the-end reset below, so a bad bookmark does not
  // fill history with a one-shot correction.
  const goTo = (p: number): void => setParams({ page: String(p) });

  const plans = usePlans(() => ({ page: page() }), token);
  const [starting, setStarting] = createSignal(false);

  // Past-the-end guard: the server answers `plans: []` with a real `total` and
  // `counts` (never a 404 — that's the documented envelope shape, the same
  // reason this branch is its OWN line and not the empty state). Treat that as
  // a stale URL, not as "the account has no plans": reset `?page=` to 1 with
  // `replace: true` so the redirect does not litter history. Gated on
  // `total > 0` on purpose: a genuinely empty account (`total === 0`) that
  // opens on a stale `?page=` also answers `plans: []`, but there is no real
  // page 1 to redirect TO — this effect never fires for it, and `isEmpty`
  // below catches that shape directly (`data.total === 0`) instead.
  createEffect(() => {
    const d = plans.data;
    if (d && d.total > 0 && d.plans.length === 0 && page() > 1) {
      setParams({ page: undefined }, { replace: true });
    }
  });

  // Shared between `QueryBoundary`'s own empty gate and the hoisted pager's
  // visibility gate below, so the two can never disagree about what counts
  // as "nothing to show yet" (#5264 review). `data.plans.length === 0 &&
  // data.page <= 1` alone missed the account-with-a-stale-bookmark shape the
  // effect above comments on — `data.total === 0` catches it regardless of
  // what page the port echoed back.
  const isEmpty = (data: PlanListPage): boolean =>
    data.plans.length === 0 && (data.page <= 1 || data.total === 0);

  // Hoisted OUTSIDE `QueryBoundary`'s `children(data)` callback — that
  // callback is a plain closure Solid re-invokes whenever `plans.data`
  // changes reference (any page landing, not just a past-the-end one),
  // producing a brand-new `<Panel>`/`<Pager>` DOM subtree every time. A
  // numbered button a keyboard reader had just activated (`aria-current`,
  // kept in the tab order by `aria-disabled` rather than `disabled`,
  // `components/Pager.tsx`) was torn out from under them the instant new
  // data arrived, dropping focus to `<body>` (#5264 review, item 3). Driven
  // by memos over `plans.data` directly instead — the same pattern
  // `CustomPlansSection.tsx` already uses to keep `<TabList/>` stable across
  // its own panel's refetches — `<Pager/>`'s DOM then persists across a page
  // change, so a click's focus is simply never removed from under it.
  const nav = serverPageNav({
    page: () => page(),
    total: () => plans.data?.total ?? 0,
    pageSize: () => plans.data?.per_page ?? 1,
    goTo,
  });
  // Shown only once there is real, in-view data to page through: never under
  // the loading/error/empty branches, and never during the past-the-end
  // reset window either — its stale-but-nonzero total would otherwise paint
  // a real "Showing" range over row content that is a loading skeleton, not
  // the rows it names.
  const showPager = (): boolean => {
    const data = plans.data;
    return data !== undefined && !plans.isError && !isEmpty(data) && !pastTheEndResetting(data);
  };

  return (
    <section class="page plans">
      <PageHeader
        title={t('fleet.plans.title')}
        desc={t('fleet.plans.subtitle')}
        actions={
          <>
            {/* The way back reads FIRST, left of the primary action: this page is
                nested under Fleet and has no rail entry of its own, so the header
                link is the only door back — labelled "Back to Fleet", the same
                sentence `<FleetAudit/>` uses for the same destination. Ordered
                after the Start button it read as a second action, and a reader
                went round through the rail instead. */}
            <A class={buttonClass({ variant: 'outline', size: 'sm' })} href={orgHref('/fleet')}>
              {t('fleet.back')}
            </A>
            {/* Signed-out renders no start control: every call it could make
                would 401, and the branch below already says so once. */}
            <Show when={token !== null}>
              <Button size="sm" onClick={() => setStarting(true)}>
                <IconPlus />
                {t('fleet.plans.start.open')}
              </Button>
            </Show>
          </>
        }
      />

      <Show when={token !== null} fallback={<EmptyState message={t('fleet.plans.no_session')} />}>
        {/* The plans a PERSON started (#4340's `?initiated_by=user`,
            #4727: `user` = a dashboard session or a credential bound to a
            person; `system` = no person is recorded as its initiator, so a
            system-initiated plan is not here), from their OWN query — ABOVE
            the paged "all plans" panel below, not a filter over it: a custom
            plan can fall outside any single page (measured 2026-09-17: 1 of
            23 on the largest account), so client-side filtering here would
            hide most of them. A plan inside both windows renders in both
            panels; the issue records that as accepted, not a bug. */}
        <CustomPlansSection token={token} />

        {/* QueryBoundary, not a bare Show: loading must not read as "empty".
            `isEmpty` (defined above, shared with the hoisted pager's own
            gate) reads `plans.length === 0 && (data.page <= 1 || data.total
            === 0)` — the ENVELOPE's own page and total, never the URL's
            `page()`: `placeholderData` (`usePlans`, lib/plan.ts) keeps the
            LAST-LANDED envelope on screen while a new page's fetch is in
            flight, and the past-the-end effect above writes `page()` back to
            1 as soon as it fires — ahead of that refetch landing. Gating on
            `page()` painted the empty state for that one window: a stale
            `plans: []`/`total > 0` envelope from the OLD (past-the-end)
            page, read against the ALREADY-RESET URL page of 1. `data.page`
            stays the stale page number until the reset's own fetch
            resolves, so this predicate can never mistake "old envelope, new
            URL" for "the account has no plans".
            The `data.total === 0` disjunct is NOT redundant with `data.page
            <= 1`: the port echoes the requested page rather than clamping
            it, so a genuinely empty account opened on a stale `?page=3`
            bookmark answers `{ plans: [], page: 3, total: 0 }` — `page <= 1`
            alone never catches that, and the past-the-end effect above never
            resets it either (it requires `total > 0`). Without this
            disjunct that response rendered an empty `<ul>` under a "0
            plans" panel heading, forever.
            A page PAST the end (`plans: []`, `total > 0`, `data.page > 1`)
            does NOT match this — that is the past-the-end effect above's
            job, paired with `pastTheEndResetting` below, which paints a
            loading skeleton over the row list instead of an empty one while
            its own refetch is in flight. */}
        <QueryBoundary
          query={plans}
          isEmpty={isEmpty}
          loading={<CardGridSkeleton listClass="plans__list" />}
          empty={
            <EmptyState
              message={t('fleet.plans.empty')}
              ctaLabel={t('fleet.plans.start.open')}
              onCta={() => setStarting(true)}
            />
          }
        >
          {(data) => (
            <Panel
              title={tPlural('fleet.plans.count', data.counts.all, {
                count: String(data.counts.all),
              })}
            >
              {/* `pastTheEndResetting`: zero rows, a page past the first, a
                  real nonzero total — the window between the past-the-end
                  effect above rewriting `?page=` to 1 and that reset's own
                  refetch landing. An empty `<ul>` there would sit under a
                  heading naming a real, nonzero count — show the loading
                  skeleton instead, the same slot every other in-flight
                  refetch already uses. */}
              <Show
                when={!pastTheEndResetting(data)}
                fallback={<CardGridSkeleton listClass="plans__list" />}
              >
                <ul class="row-list plans__list">
                  <For each={data.plans}>{(plan) => <PlanCard plan={plan} />}</For>
                </ul>
              </Show>
            </Panel>
          )}
        </QueryBoundary>
        {/* Hoisted out of `QueryBoundary`'s `children` — see `nav`/`showPager`
            above for why. `showPager` folds in the SAME `isEmpty` and
            `pastTheEndResetting` checks the row list above uses, so the
            pager is never visible while the loading/error/empty/resetting
            branches are the ones actually on screen. */}
        <Show when={showPager()}>
          <Pager pagination={nav} label={t('fleet.plans.heading')} />
        </Show>
      </Show>

      <StartPlanModal open={starting()} token={token} onClose={() => setStarting(false)} />
    </section>
  );
}
